// Strings for release 4b (0021, feature:swipe_checkin): the student
// card-deck check-in, the "Something else" concern-phrase follow-up (a
// closed vocabulary, never free text — curated per school grade by its
// head of school since release 4c, see r4cStrings.ts for that screen), and
// the teacher's results deck. Merged into STRINGS in strings.ts. The
// student-deck keys also need adding to studentStrings.ts's four extra
// languages by hand (no compile-time check there). Finnish and Swedish are
// drafts pending native-speaker review, same as every other release.
// The concern.* vocabulary itself was corrected in release 4c (0022): the
// 7 real codes replace 0021's 6 placeholders (CONCERN_CODES in
// lib/constants.ts must stay in lockstep).

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

export const r4bEn = {
  // Student card deck
  'r4b.ci.stepOf': (i: number, total: number) => `Step ${i} of ${total}`,
  'r4b.ci.back': '← Back',
  'r4b.ci.next': 'Next',
  'r4b.ci.concernTitle': 'Can you say a bit more?',
  'r4b.ci.concernHint': 'Optional — pick as many as you like, or none.',
  'r4b.ci.topicOf': (i: number, total: number) => `${i} of ${total}`,
  'r4b.ci.onYourMind': 'On your mind today?',
  'r4b.ci.onMyMind': 'On my mind',
  'r4b.ci.notReally': 'Not really',
  'r4b.ci.nothingElse': 'Nothing else',

  // The closed concern-phrase vocabulary (_concern_phrase_codes(), the
  // brief's real 7 items since release 4c). Draft pending a
  // counsellor/clinical review before a real school sees it.
  'concern.left_out_or_bullied': 'Being left out or bullied',
  'concern.online_and_phone': 'Online and my phone',
  'concern.body_or_looks': 'Body or looks',
  'concern.the_future': 'The future',
  'concern.hobbies_or_sport': 'Hobbies or sport',
  'concern.money': 'Money',
  'concern.unspecified': 'I’d rather not say',

  // Teacher's results deck (Reflect screen): slide names for the stepper
  // chrome (shared pa.wizStepOf/pa.back/pa.next) and the new concern panel.
  'r4b.ts.slideResults': 'Results',
  'r4b.ts.slidePredict': 'Prediction & you said',
  'r4b.ts.slideAct': 'Act on this',
  'r4b.ts.slideNotes': 'Notes',
  'r4b.ts.concernTitle': '“Something else” meant',
  'r4b.ts.concernInsufficient': 'Not enough students picked “Something else” yet to show a breakdown.',
};

export type R4bStrings = Shape<typeof r4bEn>;

export const r4bFi: R4bStrings = {
  'r4b.ci.stepOf': (i: number, total: number) => `Vaihe ${i}/${total}`,
  'r4b.ci.back': '← Takaisin',
  'r4b.ci.next': 'Seuraava',
  'r4b.ci.concernTitle': 'Haluatko kertoa tarkemmin?',
  'r4b.ci.concernHint': 'Vapaaehtoinen — valitse niin monta kuin haluat, tai ei yhtään.',
  'r4b.ci.topicOf': (i: number, total: number) => `${i}/${total}`,
  'r4b.ci.onYourMind': 'Onko tämä mielessäsi tänään?',
  'r4b.ci.onMyMind': 'On mielessäni',
  'r4b.ci.notReally': 'Ei oikeastaan',
  'r4b.ci.nothingElse': 'Ei muuta',

  'concern.left_out_or_bullied': 'Ulkopuolelle jättäminen tai kiusaaminen',
  'concern.online_and_phone': 'Netti ja puhelin',
  'concern.body_or_looks': 'Ulkonäkö tai keho',
  'concern.the_future': 'Tulevaisuus',
  'concern.hobbies_or_sport': 'Harrastukset tai urheilu',
  'concern.money': 'Raha',
  'concern.unspecified': 'En halua sanoa',

  'r4b.ts.slideResults': 'Tulokset',
  'r4b.ts.slidePredict': 'Ennakko-arvio ja vastasimme',
  'r4b.ts.slideAct': 'Toimenpiteet',
  'r4b.ts.slideNotes': 'Muistiinpanot',
  'r4b.ts.concernTitle': '”Jokin muu” tarkoitti',
  'r4b.ts.concernInsufficient': 'Liian vähän oppilaita valitsi ”Jokin muu” näyttääkseen erittelyn.',
};

export const r4bSv: R4bStrings = {
  'r4b.ci.stepOf': (i: number, total: number) => `Steg ${i} av ${total}`,
  'r4b.ci.back': '← Tillbaka',
  'r4b.ci.next': 'Nästa',
  'r4b.ci.concernTitle': 'Vill du berätta lite mer?',
  'r4b.ci.concernHint': 'Valfritt — välj så många du vill, eller inga.',
  'r4b.ci.topicOf': (i: number, total: number) => `${i} av ${total}`,
  'r4b.ci.onYourMind': 'Har du det här i tankarna idag?',
  'r4b.ci.onMyMind': 'Det är i mina tankar',
  'r4b.ci.notReally': 'Inte riktigt',
  'r4b.ci.nothingElse': 'Inget annat',

  'concern.left_out_or_bullied': 'Att bli utanför eller mobbad',
  'concern.online_and_phone': 'Nätet och telefonen',
  'concern.body_or_looks': 'Kroppen eller utseendet',
  'concern.the_future': 'Framtiden',
  'concern.hobbies_or_sport': 'Hobbyer eller idrott',
  'concern.money': 'Pengar',
  'concern.unspecified': 'Jag vill inte säga',

  'r4b.ts.slideResults': 'Resultat',
  'r4b.ts.slidePredict': 'Förväntning och vi svarade',
  'r4b.ts.slideAct': 'Åtgärder',
  'r4b.ts.slideNotes': 'Anteckningar',
  'r4b.ts.concernTitle': '”Något annat” betydde',
  'r4b.ts.concernInsufficient': 'För få elever valde ”Något annat” för att visa en uppdelning.',
};
